import { useEffect, useState } from "react";
import { ChevronLeft, ArrowDownToLine, ArrowUpFromLine, Landmark } from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { useAuth } from "@/context/AuthContext";
import { api, fmt, fmtDate, STATUS_TR } from "@/lib/api";
import { TrxLogo, UsdtLogo } from "@/components/CryptoLogos";
import { DepositCrypto } from "@/components/wallet/DepositCrypto";
import { DepositBank } from "@/components/wallet/DepositBank";
import { WithdrawForm } from "@/components/wallet/WithdrawForm";

const TITLES = {
  main: "Cüzdan", depositMethod: "Para Yatır", depositCrypto: "Kripto ile Yatır", depositBank: "Havale / EFT",
  withdrawMethod: "Para Çek", withdrawCrypto: "Kripto ile Çek", withdrawBank: "Havale / EFT ile Çek",
};
const BACK = { depositMethod: "main", withdrawMethod: "main", depositCrypto: "depositMethod", depositBank: "depositMethod", withdrawCrypto: "withdrawMethod", withdrawBank: "withdrawMethod" };

const MethodBtn = ({ testId, onClick, icon, title, sub }) => (
  <button data-testid={testId} onClick={onClick} className="w-full x-panel hover:bg-[var(--x-panel-2)] transition-colors p-4 flex items-center gap-4 text-left">
    <div className="flex -space-x-2">{icon}</div>
    <div className="flex-1">
      <div className="font-extrabold">{title}</div>
      <div className="text-[13px] text-[var(--x-muted)]">{sub}</div>
    </div>
    <ChevronLeft size={18} className="rotate-180 text-[var(--x-muted)]" />
  </button>
);

const CryptoIcons = () => (<><TrxLogo size={34} /><UsdtLogo size={34} /></>);
const BankIcon = () => (<div className="w-[34px] h-[34px] rounded-full bg-[var(--x-blue)] flex items-center justify-center"><Landmark size={18} /></div>);

const TxList = ({ items }) => (
  <div className="space-y-2" data-testid="wallet-tx-list">
    {items.length === 0 && <div className="text-center text-sm text-[var(--x-dim)] py-4">Henüz işlem yok</div>}
    {items.map((t) => {
      const st = STATUS_TR[t.status] || { t: t.status, c: "#fff" };
      return (
        <div key={t.id} className="flex items-center justify-between bg-[var(--x-bg-deep)] rounded-md px-3 py-2.5 text-sm">
          <div>
            <div className="font-bold">{t.kind === "deposit" ? "Yatırım" : "Çekim"} · {t.method === "crypto" ? t.coin : "Havale/EFT"}</div>
            <div className="text-[12px] text-[var(--x-dim)]">{fmtDate(t.created_at)}</div>
          </div>
          <div className="text-right">
            <div className="font-bold tabular-nums">₺{fmt(t.amount_try)}</div>
            <div className="text-[12px] font-bold" style={{ color: st.c }}>{st.t}</div>
          </div>
        </div>
      );
    })}
  </div>
);

export const WalletSheet = () => {
  const { user, walletOpen, setWalletOpen, refresh } = useAuth();
  const [view, setView] = useState("main");
  const [txs, setTxs] = useState([]);

  useEffect(() => {
    if (walletOpen && user) {
      setView("main");
      refresh();
      api.get("/user/transactions").then((r) => setTxs(r.data)).catch(() => {});
    }
  }, [walletOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => setWalletOpen(false);
  if (!user) return null;

  return (
    <Drawer open={walletOpen} onOpenChange={setWalletOpen} shouldScaleBackground={false} repositionInputs={false}>
      <DrawerContent data-testid="wallet-sheet" className="bg-[var(--x-bg)] border-[var(--x-border)] max-h-[90dvh] mt-0">
        <div className="max-w-lg w-full mx-auto px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] overflow-y-auto no-scrollbar min-h-0 flex-1">
          <div className="flex items-center gap-2 pt-3 pb-4 sticky top-0 bg-[var(--x-bg)] z-10">
            {view !== "main" && (
              <button data-testid="wallet-back-btn" onClick={() => setView(BACK[view])} className="p-1.5 rounded-md hover:bg-white/5">
                <ChevronLeft size={20} />
              </button>
            )}
            <DrawerTitle className="text-lg font-extrabold">{TITLES[view]}</DrawerTitle>
            <DrawerDescription className="sr-only">Cüzdan işlemleri</DrawerDescription>
          </div>

          {view === "main" && (
            <div className="x-rise">
              <div className="text-center py-6">
                <div className="text-[13px] font-semibold text-[var(--x-muted)] uppercase tracking-wider">Bakiye</div>
                <div data-testid="wallet-balance" className="text-4xl sm:text-5xl font-black tabular-nums mt-2">₺{fmt(user.balance)}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button data-testid="wallet-deposit-btn" onClick={() => setView("depositMethod")} className="x-btn x-btn-primary py-3.5">
                  <ArrowDownToLine size={18} /> Para Yatır
                </button>
                <button data-testid="wallet-withdraw-btn" onClick={() => setView("withdrawMethod")} className="x-btn x-btn-secondary py-3.5">
                  <ArrowUpFromLine size={18} /> Para Çek
                </button>
              </div>
              <div className="mt-7 mb-2 text-sm font-bold text-[var(--x-muted)]">Son İşlemler</div>
              <TxList items={txs} />
            </div>
          )}

          {view === "depositMethod" && (
            <div className="space-y-3 x-rise">
              <MethodBtn testId="deposit-method-crypto" onClick={() => setView("depositCrypto")} icon={<CryptoIcons />} title="Kripto" sub="TRON (TRX) · Tether (USDT TRC20) — otomatik onay" />
              <MethodBtn testId="deposit-method-bank" onClick={() => setView("depositBank")} icon={<BankIcon />} title="Havale / EFT" sub="Banka transferi — manuel onay" />
            </div>
          )}
          {view === "withdrawMethod" && (
            <div className="space-y-3 x-rise">
              <MethodBtn testId="withdraw-method-crypto" onClick={() => setView("withdrawCrypto")} icon={<CryptoIcons />} title="Kripto" sub="TRX veya USDT (TRC20) adresine" />
              <MethodBtn testId="withdraw-method-bank" onClick={() => setView("withdrawBank")} icon={<BankIcon />} title="Havale / EFT" sub="Banka hesabınıza" />
            </div>
          )}
          {view === "depositCrypto" && <DepositCrypto onDone={close} />}
          {view === "depositBank" && <DepositBank onDone={close} />}
          {view === "withdrawCrypto" && <WithdrawForm method="crypto" onDone={close} />}
          {view === "withdrawBank" && <WithdrawForm method="bank" onDone={close} />}
        </div>
      </DrawerContent>
    </Drawer>
  );
};
